// src/pages/SalarySummary.js
import React, { useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import { Form, Alert, Card, Spinner, Badge } from "react-bootstrap";

const SalarySummary = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [targetName, setTargetName] = useState(user?.name || "");
  const [error, setError] = useState("");
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmp, setLoadingEmp] = useState(false);
  const isAdmin = user?.role === "Admin";
  const isManager = user?.role === "Manager";

  useEffect(() => {
    if (!user || (!isAdmin && !isManager)) return;
    setLoadingEmp(true);
    api.get("/employees").then((res) => setEmployees(res.data || [])).catch(() => {}).finally(() => setLoadingEmp(false));
  }, [user, isAdmin, isManager]);

  useEffect(() => {
    if (!targetName) return;
    setLoadingSummary(true);
    api.get(`/commissions/summary/${encodeURIComponent(targetName)}`)
      .then((res) => { setSummary(res.data); setError(""); })
      .catch(() => { setSummary(null); setError("User not found or no summary available."); })
      .finally(() => setLoadingSummary(false));
  }, [targetName]);

  const selectable = (() => {
    if (!user) return [];
    if (isAdmin) return employees;
    if (isManager) {
      const team = employees.filter((e) => e.manager === user.name);
      const self = employees.find((e) => e.email === user.email);
      return [...(self ? [self] : []), ...team.filter((e) => !self || e.email !== self.email)];
    }
    return [employees.find((e) => e.email === user.email) || { name: user.name, role: user.role }];
  })();

  useEffect(() => { if (!targetName && user?.name) setTargetName(user.name); }, [user, targetName]);

  return (
    <div className="container-fluid px-4 py-4">
      <h3 className="fw-bold mb-1">Salary & Commission Summary</h3>
      <div className="text-muted small mb-3">View payout summary based on closed customers and commission plan.</div>

      {(isAdmin || isManager) && (
        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap salary-view-row">
          <span className="small text-muted">View for:</span>
          <Form.Select className="salary-view-select" value={targetName} onChange={(e) => setTargetName(e.target.value)}>
            {selectable.map((e) => <option key={e._id || e.email || e.name} value={e.name}>{e.name} {e.role && `(${e.role})`}</option>)}
          </Form.Select>
          {loadingEmp && <Spinner animation="border" size="sm" />}
        </div>
      )}

      {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

      <Card className="shadow-sm border-0">
        <Card.Body>
          {loadingSummary ? (
            <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span className="small text-muted">Loading…</span></div>
          ) : summary ? (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div><h5 className="mb-1">{summary.employee} <Badge bg="secondary" className="ms-1">{summary.role}</Badge></h5><div className="small text-muted">Total payout = Fixed salary + commission on closed customers.</div></div>
              </div>
              <hr />
              <div className="row g-3">
                <div className="col-md-3"><div className="small text-muted">Total Sales</div><div className="fs-4 fw-bold">{summary.totalSales}</div></div>
                <div className="col-md-3"><div className="small text-muted">Fixed Salary</div><div className="fs-5 fw-semibold text-primary">${Number(summary.fixedSalary).toLocaleString()}</div></div>
                <div className="col-md-3"><div className="small text-muted">Commission</div><div className="fs-5 fw-semibold text-warning">${Number(summary.totalCommission).toLocaleString()}</div></div>
                <div className="col-md-3"><div className="small text-muted">Total Payout</div><div className="fs-4 fw-bold text-success">${Number(summary.totalPayout).toLocaleString()}</div></div>
              </div>
            </>
          ) : !loadingSummary && <div className="small text-muted">No summary data available.</div>}
        </Card.Body>
      </Card>
    </div>
  );
};

export default SalarySummary;